import { PROJECT_IDS_MAX } from '@aica/api-types';
import {
  ANALYZED_CALL_CATEGORIES,
  OBJECTION_STREAM_MAX_WINDOW_DAYS,
  type AnalyzedCallCategory,
} from '@aica/api-types/schemas';
import type { useAppNavigate } from '@/hooks/useAppNavigate';

/**
 * Параметры адреса экрана «Возражения» (#846) — всё состояние экрана:
 * период с опорной датой, проекты, категория, взгляд и раскрытый код. В
 * адресе, а не в состоянии компонента: цитата открывает разговор, и «Назад»
 * должно вернуть человека туда, где он был, а не к фильтру ссылки.
 *
 * Общие для портала (`/analytics/objections`) и встроенной вкладки Bitrix24,
 * чтобы обе оболочки понимали одну ссылку.
 */
export const OBJECTIONS_PERIODS = ['today', 'week', 'month', 'quarter'] as const;
export type ObjectionsPeriod = (typeof OBJECTIONS_PERIODS)[number];

export const OBJECTIONS_VIEWS = ['market', 'team'] as const;
export type ObjectionsViewMode = (typeof OBJECTIONS_VIEWS)[number];

export interface ObjectionsSearch {
  period?: ObjectionsPeriod;
  /** Any instant inside the shown period (ISO) — the dashboard's, when opened from it. */
  anchor?: string;
  projectIds?: string[];
  category?: AnalyzedCallCategory;
  view?: ObjectionsViewMode;
  /** The expanded code. */
  code?: string;
  /**
   * An exact window (ISO instants) from an email link — the daily digest
   * opens the screen on the org's yesterday (#1211), which the viewer's
   * timezone would draw differently. See `StreamSearch`.
   */
  from?: string;
  to?: string;
  /**
   * The calendar day ('YYYY-MM-DD') the exact window covers, in the zone of
   * the organization the email is about. The period bar names this day, not
   * the viewer's date of the window's midpoint. Read only together with
   * `from`/`to`: it goes with them on any step or period change.
   */
  day?: string;
  /**
   * The demo rule the link was counted by (the unified dashboard's «Отчёт →»):
   * absent — the organization's policy, as every report. The dashboard's own
   * filter can override that policy, and a report that ignored the override
   * would count other conversations than the card did.
   */
  demo?: 'yes' | 'no';
}

export const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Both bounds or neither: valid instants, in order, no wider than
 * `maxDays` — the route's limit; anything else would be a permanent 400 that
 * Retry cannot fix.
 */
export function exactWindow(
  from: unknown,
  to: unknown,
  maxDays: number
): { from: string; to: string } | null {
  if (typeof from !== 'string' || typeof to !== 'string') return null;
  if (from.length > 40 || to.length > 40) return null;
  const start = Date.parse(from);
  const end = Date.parse(to);
  if (Number.isNaN(start) || Number.isNaN(end) || start > end) return null;
  if (end - start > maxDays * DAY_MS) return null;
  return { from: new Date(start).toISOString(), to: new Date(end).toISOString() };
}

/** A real calendar date 'YYYY-MM-DD' — anything else is not a label to draw. */
export function calendarDay(value: unknown): string | undefined {
  return dayParts(value) ? (value as string) : undefined;
}

/** Year, month (1-12) and day of a real calendar date 'YYYY-MM-DD'. */
export function dayParts(value: unknown): [number, number, number] | undefined {
  const match =
    typeof value === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value) : null;
  if (!match) return undefined;
  const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const probe = new Date(Date.UTC(y, m - 1, d));
  return probe.getUTCFullYear() === y &&
    probe.getUTCMonth() === m - 1 &&
    probe.getUTCDate() === d
    ? [y, m, d]
    : undefined;
}

const DEMO_RULES = ['yes', 'no'] as const;

/** Коды — короткие snake_case ключи; набор кодов клиент может знать не весь. */
const KEY_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;

function stringParam(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * The projects of an address: a string from a link ("a,b"), or an array once
 * the router has re-serialized the parsed search on a later navigation. Kept to
 * the filter's own cap (the route rejects more), not a copy of it.
 */
export function projectIdsParam(raw: unknown): string[] {
  const items = Array.isArray(raw)
    ? raw.filter((id): id is string => typeof id === 'string')
    : typeof raw === 'string'
      ? raw.split(',')
      : [];
  return items
    .map((id) => id.trim())
    .filter((id) => id.length > 0 && id.length <= 100)
    .slice(0, PROJECT_IDS_MAX);
}

export function parseObjectionsSearch(
  search: Record<string, unknown>,
  /** The route's own window limit; the stream's by default. */
  maxWindowDays: number = OBJECTION_STREAM_MAX_WINDOW_DAYS
): ObjectionsSearch {
  const period = OBJECTIONS_PERIODS.find((p) => p === search.period);
  const view = OBJECTIONS_VIEWS.find((v) => v === search.view);
  const anchor = stringParam(search.anchor);
  // Only a category the stream route accepts: any other value would be a
  // permanent 400 that Retry cannot fix.
  const category = ANALYZED_CALL_CATEGORIES.find((c) => c === search.category);
  const code = stringParam(search.code);
  const exact = exactWindow(search.from, search.to, maxWindowDays);
  const day = calendarDay(search.day);
  const demo = DEMO_RULES.find((rule) => rule === search.demo);
  const projectIds = projectIdsParam(search.projectIds);
  return {
    ...(period ? { period } : {}),
    ...(anchor && anchor.length <= 40 && !Number.isNaN(Date.parse(anchor))
      ? { anchor }
      : {}),
    ...(projectIds.length > 0 ? { projectIds } : {}),
    ...(category ? { category } : {}),
    ...(view ? { view } : {}),
    ...(code && KEY_PATTERN.test(code) ? { code } : {}),
    ...(exact ?? {}),
    ...(exact && day ? { day } : {}),
    ...(demo ? { demo } : {}),
  };
}

/**
 * Параметры для ссылки из карточки дашборда на экран потока: только то, что
 * отличает её фильтр. Один сборщик на все экраны, куда ведёт дашборд (возражения,
 * шаги скрипта), — иначе переход с дашборда расходится в том, что считать верным
 * значением.
 */
export function streamSearchParams(filter: {
  period?: string;
  projectIds?: readonly string[];
  anchor?: string;
}): Record<string, string> {
  const params: Record<string, string> = {};
  if (filter.period && OBJECTIONS_PERIODS.some((p) => p === filter.period)) {
    params.period = filter.period;
  }
  if (filter.anchor) params.anchor = filter.anchor;
  if (filter.projectIds && filter.projectIds.length > 0) {
    params.projectIds = filter.projectIds.join(',');
  }
  return params;
}

/**
 * Открыть экран с фильтром карточки — тем же периодом (с опорной датой: на
 * дашборде могли уйти стрелкой в прошлый месяц) и теми же проектами. В портале
 * — страницей, в Bitrix24 — вкладкой встроенного дашборда (без неё
 * `useAppNavigate` открыл бы портал в новом окне, где у пользователя CRM нет
 * сессии).
 */
export function openObjectionsScreen(
  appNavigate: ReturnType<typeof useAppNavigate>['appNavigate'],
  filter: { period?: string; projectIds?: readonly string[]; anchor?: string }
): void {
  const params = streamSearchParams(filter);
  const query = new URLSearchParams(params).toString();
  appNavigate(`/analytics/objections${query ? `?${query}` : ''}`, {
    embeddedTo: { to: '/embedded/dashboard/', search: { tab: 'objections', ...params } },
  });
}
